import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { Network, ZoomIn, ZoomOut, RotateCcw, Maximize2 } from 'lucide-react';
import { ADMIN_COLORS, staggerContainer, staggerItem } from '../utils/adminAnimations';

// ─── Entity definitions ────────────────────────────────────

interface Field {
  name: string;
  type: string;
  pk?: boolean;
  fk?: boolean;
}

interface Entity {
  id: string;
  label: string;
  color: string;
  x: number;
  y: number;
  fields: Field[];
}

interface Relation {
  from: string;
  to: string;
  fromField: string;
  toField: string;
  label: string;
  type: '1:N' | 'N:1' | '1:1' | 'N:M';
}

const ENTITIES: Entity[] = [
  // ── Core ──
  {
    id: 'User',
    label: 'User',
    color: '#1565c0',
    x: 480,
    y: 40,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'email', type: 'String' },
      { name: 'role', type: 'String' },
      { name: 'roleId', type: 'String?', fk: true },
      { name: 'employmentRoleId', type: 'String?', fk: true },
      { name: 'customBaseRate', type: 'Float?' },
      { name: 'status', type: 'String' },
    ],
  },
  {
    id: 'Role',
    label: 'Role (System)',
    color: '#6a1b9a',
    x: 80,
    y: 40,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'code', type: 'String' },
      { name: 'name', type: 'String' },
      { name: 'isSystem', type: 'Boolean' },
    ],
  },
  {
    id: 'RolePermission',
    label: 'RolePermission',
    color: '#6a1b9a',
    x: 80,
    y: 220,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'roleId', type: 'String', fk: true },
      { name: 'permission', type: 'String' },
    ],
  },
  // ── Employment ──
  {
    id: 'EmploymentRole',
    label: 'EmploymentRole',
    color: '#00695c',
    x: 880,
    y: 40,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'code', type: 'String' },
      { name: 'name', type: 'String' },
      { name: 'baseRate', type: 'Float' },
      { name: 'baseRatePeriod', type: 'String' },
    ],
  },
  {
    id: 'EmploymentRoleRate',
    label: 'EmpRoleRate',
    color: '#00695c',
    x: 1080,
    y: 220,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'employmentRoleId', type: 'String', fk: true },
      { name: 'reportTypeId', type: 'String', fk: true },
      { name: 'ratePerUnit', type: 'Float' },
      { name: 'fieldOverrides', type: 'JSON?' },
    ],
  },
  // ── Salary Rules & Allowances ──
  {
    id: 'EmploymentRoleHospitalBonus',
    label: 'EmpRoleHospBonus',
    color: '#00695c',
    x: 880,
    y: 270,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'employmentRoleRateId', type: 'String', fk: true },
      { name: 'hospitalId', type: 'String', fk: true },
      { name: 'bonusAmount', type: 'Float' },
    ],
  },
  {
    id: 'EmploymentRoleAllowance',
    label: 'EmpRoleAllowance',
    color: '#00695c',
    x: 880,
    y: 420,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'employmentRoleId', type: 'String', fk: true },
      { name: 'allowanceTypeId', type: 'String', fk: true },
      { name: 'amount', type: 'Float' },
      { name: 'period', type: 'String' },
    ],
  },
  {
    id: 'AllowanceType',
    label: 'AllowanceType',
    color: '#4e342e',
    x: 30,
    y: 530,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'code', type: 'String' },
      { name: 'name', type: 'String' },
      { name: 'isTaxable', type: 'Boolean' },
      { name: 'isRecurring', type: 'Boolean' },
    ],
  },
  // ── Reports ──
  {
    id: 'Report',
    label: 'Report',
    color: '#283593',
    x: 480,
    y: 530,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'therapistId', type: 'String', fk: true },
      { name: 'hospitalId', type: 'String', fk: true },
      { name: 'reportTypeId', type: 'String?', fk: true },
      { name: 'reportTypeCode', type: 'String' },
      { name: 'date', type: 'String' },
      { name: 'status', type: 'String' },
    ],
  },
  {
    id: 'ReportType',
    label: 'ReportType',
    color: '#283593',
    x: 880,
    y: 600,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'code', type: 'String' },
      { name: 'name', type: 'String' },
      { name: 'unit', type: 'String' },
    ],
  },
  {
    id: 'ReportTypeField',
    label: 'ReportTypeField',
    color: '#283593',
    x: 1080,
    y: 750,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'reportTypeId', type: 'String', fk: true },
      { name: 'fieldKey', type: 'String' },
      { name: 'fieldType', type: 'String' },
    ],
  },
  // ── Hospital ──
  {
    id: 'Hospital',
    label: 'Hospital',
    color: '#2e7d32',
    x: 80,
    y: 700,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'name', type: 'String' },
      { name: 'code', type: 'String' },
      { name: 'city', type: 'String' },
    ],
  },
  {
    id: 'UserHospital',
    label: 'UserHospital',
    color: '#2e7d32',
    x: 240,
    y: 850,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'therapistId', type: 'String', fk: true },
      { name: 'hospitalId', type: 'String', fk: true },
      { name: 'isPrimary', type: 'Boolean' },
    ],
  },
  // ── Treatments ──
  {
    id: 'TreatmentType',
    label: 'TreatmentType',
    color: '#ad1457',
    x: 480,
    y: 800,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'code', type: 'String' },
      { name: 'name', type: 'String' },
    ],
  },
  {
    id: 'HelmetType',
    label: 'HelmetType',
    color: '#ad1457',
    x: 700,
    y: 900,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'code', type: 'String' },
      { name: 'name', type: 'String' },
    ],
  },
  {
    id: 'TreatmentTypeHelmetType',
    label: 'Treatment↔Helmet',
    color: '#ad1457',
    x: 480,
    y: 960,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'treatmentTypeId', type: 'String', fk: true },
      { name: 'helmetTypeId', type: 'String', fk: true },
    ],
  },
  // ── Payroll ──
  {
    id: 'PayrollRun',
    label: 'PayrollRun',
    color: '#0d47a1',
    x: 80,
    y: 940,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'month', type: 'String' },
      { name: 'status', type: 'String' },
      { name: 'generatedById', type: 'String', fk: true },
      { name: 'approvedById', type: 'String?', fk: true },
    ],
  },
  {
    id: 'PayrollEntry',
    label: 'PayrollEntry',
    color: '#0d47a1',
    x: 280,
    y: 1100,
    fields: [
      { name: 'id', type: 'String', pk: true },
      { name: 'payrollRunId', type: 'String', fk: true },
      { name: 'userId', type: 'String', fk: true },
      { name: 'employmentRoleId', type: 'String?', fk: true },
      { name: 'employmentRoleRateId', type: 'String?', fk: true },
      { name: 'entryType', type: 'String' },
      { name: 'amount', type: 'Float' },
    ],
  },
];

const RELATIONS: Relation[] = [
  // User ← Role
  { from: 'User', to: 'Role', fromField: 'roleId', toField: 'id', label: 'system role', type: 'N:1' },
  // Role → RolePermission
  { from: 'Role', to: 'RolePermission', fromField: 'id', toField: 'roleId', label: 'permissions', type: '1:N' },
  // User ← EmploymentRole
  { from: 'User', to: 'EmploymentRole', fromField: 'employmentRoleId', toField: 'id', label: 'employment role', type: 'N:1' },
  // EmploymentRole → EmploymentRoleRate
  { from: 'EmploymentRole', to: 'EmploymentRoleRate', fromField: 'id', toField: 'employmentRoleId', label: 'rates', type: '1:N' },
  // EmploymentRoleRate ← ReportType
  { from: 'EmploymentRoleRate', to: 'ReportType', fromField: 'reportTypeId', toField: 'id', label: '', type: 'N:1' },
  // EmploymentRoleRate → EmploymentRoleHospitalBonus
  { from: 'EmploymentRoleRate', to: 'EmploymentRoleHospitalBonus', fromField: 'id', toField: 'employmentRoleRateId', label: 'bonuses', type: '1:N' },
  // EmploymentRoleHospitalBonus ← Hospital
  { from: 'EmploymentRoleHospitalBonus', to: 'Hospital', fromField: 'hospitalId', toField: 'id', label: '', type: 'N:1' },
  // EmploymentRole → EmploymentRoleAllowance
  { from: 'EmploymentRole', to: 'EmploymentRoleAllowance', fromField: 'id', toField: 'employmentRoleId', label: 'allowances', type: '1:N' },
  // EmploymentRoleAllowance ← AllowanceType
  { from: 'EmploymentRoleAllowance', to: 'AllowanceType', fromField: 'allowanceTypeId', toField: 'id', label: '', type: 'N:1' },
  // User → Report
  { from: 'User', to: 'Report', fromField: 'id', toField: 'therapistId', label: 'reports', type: '1:N' },
  // Report ← Hospital
  { from: 'Report', to: 'Hospital', fromField: 'hospitalId', toField: 'id', label: '', type: 'N:1' },
  // Report ← ReportType
  { from: 'Report', to: 'ReportType', fromField: 'reportTypeId', toField: 'id', label: '', type: 'N:1' },
  // ReportType → ReportTypeField
  { from: 'ReportType', to: 'ReportTypeField', fromField: 'id', toField: 'reportTypeId', label: 'fields', type: '1:N' },
  // User ↔ Hospital (M:N via UserHospital)
  { from: 'User', to: 'UserHospital', fromField: 'id', toField: 'therapistId', label: '', type: '1:N' },
  { from: 'Hospital', to: 'UserHospital', fromField: 'id', toField: 'hospitalId', label: '', type: '1:N' },
  // TreatmentType ↔ HelmetType (M:N)
  { from: 'TreatmentType', to: 'TreatmentTypeHelmetType', fromField: 'id', toField: 'treatmentTypeId', label: '', type: '1:N' },
  { from: 'HelmetType', to: 'TreatmentTypeHelmetType', fromField: 'id', toField: 'helmetTypeId', label: '', type: '1:N' },
  // PayrollRun ← User (generatedBy)
  { from: 'PayrollRun', to: 'User', fromField: 'generatedById', toField: 'id', label: 'generated by', type: 'N:1' },
  // PayrollRun → PayrollEntry
  { from: 'PayrollRun', to: 'PayrollEntry', fromField: 'id', toField: 'payrollRunId', label: 'entries', type: '1:N' },
  // PayrollEntry ← User
  { from: 'PayrollEntry', to: 'User', fromField: 'userId', toField: 'id', label: '', type: 'N:1' },
  // PayrollEntry ← EmploymentRole
  { from: 'PayrollEntry', to: 'EmploymentRole', fromField: 'employmentRoleId', toField: 'id', label: '', type: 'N:1' },
  // PayrollEntry ← EmploymentRoleRate (was EmploymentRoleSalaryRule)
  { from: 'PayrollEntry', to: 'EmploymentRoleRate', fromField: 'employmentRoleRateId', toField: 'id', label: '', type: 'N:1' },
];

// ─── Dimensions ────────────────────────────────────────

const ENTITY_W = 220;
const FIELD_H = 18;
const HEADER_H = 28;
const PADDING = 8;

function entityHeight(e: Entity) {
  return HEADER_H + e.fields.length * FIELD_H + PADDING;
}

// ─── Connection drawing ────────────────────────────────

function getAnchor(e: Entity, side: 'left' | 'right' | 'top' | 'bottom') {
  const h = entityHeight(e);
  switch (side) {
    case 'left':
      return { x: e.x, y: e.y + h / 2 };
    case 'right':
      return { x: e.x + ENTITY_W, y: e.y + h / 2 };
    case 'top':
      return { x: e.x + ENTITY_W / 2, y: e.y };
    case 'bottom':
      return { x: e.x + ENTITY_W / 2, y: e.y + h };
  }
}

function bestSides(from: Entity, to: Entity): { fromSide: 'left' | 'right' | 'top' | 'bottom'; toSide: 'left' | 'right' | 'top' | 'bottom' } {
  const fcx = from.x + ENTITY_W / 2;
  const fcy = from.y + entityHeight(from) / 2;
  const tcx = to.x + ENTITY_W / 2;
  const tcy = to.y + entityHeight(to) / 2;
  const dx = tcx - fcx;
  const dy = tcy - fcy;

  if (Math.abs(dx) > Math.abs(dy)) {
    return dx > 0
      ? { fromSide: 'right', toSide: 'left' }
      : { fromSide: 'left', toSide: 'right' };
  }
  return dy > 0
    ? { fromSide: 'bottom', toSide: 'top' }
    : { fromSide: 'top', toSide: 'bottom' };
}

function cardinalityMarker(type: '1:N' | 'N:1' | '1:1' | 'N:M', end: 'from' | 'to') {
  if (type === '1:1') return '1';
  if (type === '1:N') return end === 'from' ? '1' : 'N';
  if (type === 'N:1') return end === 'from' ? 'N' : '1';
  return 'N';
}

function RelationLine({ rel, entities }: { rel: Relation; entities: Entity[] }) {
  const from = entities.find((e) => e.id === rel.from);
  const to = entities.find((e) => e.id === rel.to);
  if (!from || !to) return null;

  const { fromSide, toSide } = bestSides(from, to);
  const a = getAnchor(from, fromSide);
  const b = getAnchor(to, toSide);

  // Cubic bezier
  const isHorizontal = fromSide === 'left' || fromSide === 'right';
  let c1x: number, c1y: number, c2x: number, c2y: number;
  if (isHorizontal) {
    const spread = Math.abs(b.x - a.x) * 0.4;
    c1x = fromSide === 'right' ? a.x + spread : a.x - spread;
    c1y = a.y;
    c2x = toSide === 'right' ? b.x + spread : b.x - spread;
    c2y = b.y;
  } else {
    const spread = Math.abs(b.y - a.y) * 0.4;
    c1x = a.x;
    c1y = fromSide === 'bottom' ? a.y + spread : a.y - spread;
    c2x = b.x;
    c2y = toSide === 'bottom' ? b.y + spread : b.y - spread;
  }

  const midX = (a.x + b.x) / 2;
  const midY = (a.y + b.y) / 2;

  const fromMark = cardinalityMarker(rel.type, 'from');
  const toMark = cardinalityMarker(rel.type, 'to');

  return (
    <g>
      <path
        d={`M ${a.x} ${a.y} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${b.x} ${b.y}`}
        fill="none"
        stroke="#94a3b8"
        strokeWidth={1.5}
        strokeDasharray={rel.type === 'N:M' ? '4 2' : undefined}
      />
      {/* Cardinality markers */}
      <text x={a.x + (fromSide === 'right' ? 8 : fromSide === 'left' ? -8 : 6)} y={a.y + (fromSide === 'bottom' ? 14 : fromSide === 'top' ? -6 : -4)} fontSize={10} fill="#64748b" textAnchor={fromSide === 'left' ? 'end' : 'start'} fontWeight={600}>
        {fromMark}
      </text>
      <text x={b.x + (toSide === 'right' ? 8 : toSide === 'left' ? -8 : 6)} y={b.y + (toSide === 'bottom' ? 14 : toSide === 'top' ? -6 : -4)} fontSize={10} fill="#64748b" textAnchor={toSide === 'left' ? 'end' : 'start'} fontWeight={600}>
        {toMark}
      </text>
      {/* Label */}
      {rel.label && (
        <text x={midX} y={midY - 6} fontSize={9} fill="#94a3b8" textAnchor="middle" fontStyle="italic">
          {rel.label}
        </text>
      )}
    </g>
  );
}

// ─── Entity box ────────────────────────────────────────

function EntityBox({ entity }: { entity: Entity }) {
  const h = entityHeight(entity);
  return (
    <g>
      {/* Shadow */}
      <rect x={entity.x + 3} y={entity.y + 3} width={ENTITY_W} height={h} rx={8} fill={ADMIN_COLORS.navy} opacity={0.25} />
      {/* Body */}
      <rect x={entity.x} y={entity.y} width={ENTITY_W} height={h} rx={8} fill="white" stroke={entity.color} strokeWidth={2} />
      {/* Header */}
      <rect x={entity.x} y={entity.y} width={ENTITY_W} height={HEADER_H} rx={8} fill={entity.color} />
      <rect x={entity.x} y={entity.y + HEADER_H - 8} width={ENTITY_W} height={8} fill={entity.color} />
      <text x={entity.x + ENTITY_W / 2} y={entity.y + 18} textAnchor="middle" fill="white" fontSize={12} fontWeight={700} fontFamily="Inter, sans-serif">
        {entity.label}
      </text>
      {/* Fields */}
      {entity.fields.map((f, i) => {
        const fy = entity.y + HEADER_H + i * FIELD_H + 14;
        return (
          <g key={f.name}>
            {f.pk && (
              <text x={entity.x + 8} y={fy} fontSize={9} fill="#f59e0b" fontWeight={700}>
                PK
              </text>
            )}
            {f.fk && !f.pk && (
              <text x={entity.x + 8} y={fy} fontSize={9} fill="#6366f1" fontWeight={700}>
                FK
              </text>
            )}
            <text x={entity.x + (f.pk || f.fk ? 28 : 10)} y={fy} fontSize={11} fill="#334155" fontFamily="monospace">
              {f.name}
            </text>
            <text x={entity.x + ENTITY_W - 8} y={fy} fontSize={10} fill="#94a3b8" textAnchor="end" fontFamily="monospace">
              {f.type}
            </text>
          </g>
        );
      })}
    </g>
  );
}

// ─── Legend ─────────────────────────────────────────────

const LEGEND_ITEMS = [
  { color: '#1565c0', label: 'User / Auth' },
  { color: '#6a1b9a', label: 'System Roles' },
  { color: '#00695c', label: 'Employment' },
  { color: '#283593', label: 'Reports' },
  { color: '#2e7d32', label: 'Hospitals' },
  { color: '#ad1457', label: 'Treatment/Helmet' },
  { color: '#0d47a1', label: 'Payroll' },
  { color: '#4e342e', label: 'Allowance' },
];

// ─── Main Component ────────────────────────────────────

export function RelationsPage() {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  // viewOrigin = top-left corner in SVG coordinate space
  const [viewOrigin, setViewOrigin] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [containerSize, setContainerSize] = useState({ w: 1200, h: 800 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });

  // Track container size with ResizeObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerSize({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // viewBox derived from zoom + origin
  const viewBox = `${viewOrigin.x} ${viewOrigin.y} ${containerSize.w / zoom} ${containerSize.h / zoom}`;

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    setZoom((z) => Math.min(2, Math.max(0.15, z + delta)));
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      setDragging(true);
      dragStart.current = { x: e.clientX, y: e.clientY, ox: viewOrigin.x, oy: viewOrigin.y };
    },
    [viewOrigin],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging) return;
      // Screen pixels → SVG coordinates (divide by zoom)
      setViewOrigin({
        x: dragStart.current.ox - (e.clientX - dragStart.current.x) / zoom,
        y: dragStart.current.oy - (e.clientY - dragStart.current.y) / zoom,
      });
    },
    [dragging, zoom],
  );

  const handleMouseUp = useCallback(() => setDragging(false), []);

  // Touch support
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length !== 1) return;
      const touch = e.touches[0];
      setDragging(true);
      dragStart.current = { x: touch.clientX, y: touch.clientY, ox: viewOrigin.x, oy: viewOrigin.y };
    },
    [viewOrigin],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!dragging || e.touches.length !== 1) return;
      const touch = e.touches[0];
      setViewOrigin({
        x: dragStart.current.ox - (touch.clientX - dragStart.current.x) / zoom,
        y: dragStart.current.oy - (touch.clientY - dragStart.current.y) / zoom,
      });
    },
    [dragging, zoom],
  );

  const fitView = useCallback(() => {
    const cw = containerSize.w || 1200;
    const ch = containerSize.h || 800;
    const pad = 40;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const e of ENTITIES) {
      minX = Math.min(minX, e.x);
      minY = Math.min(minY, e.y);
      maxX = Math.max(maxX, e.x + ENTITY_W);
      maxY = Math.max(maxY, e.y + entityHeight(e));
    }
    const contentW = maxX - minX + pad * 2;
    const contentH = maxY - minY + pad * 2;
    const fitZoom = Math.min(cw / contentW, ch / contentH, 1.5);
    setZoom(fitZoom);
    // Center the content
    setViewOrigin({
      x: minX - pad - (cw / fitZoom - contentW) / 2,
      y: minY - pad - (ch / fitZoom - contentH) / 2,
    });
  }, [containerSize]);

  const resetView = useCallback(() => {
    fitView();
  }, [fitView]);

  // Fit on mount + when container resizes
  useEffect(() => {
    fitView();
  }, [fitView]);

  return (
    <motion.div
      dir="ltr"
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-4"
    >
      {/* Header */}
      <motion.div variants={staggerItem} className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl glass-surface"
            style={{ backgroundColor: ADMIN_COLORS.surface, border: `1px solid ${ADMIN_COLORS.border}` }}
          >
            <Network className="h-5 w-5" style={{ color: ADMIN_COLORS.navy }} />
          </div>
          <div>
            <h1 className="text-lg sm:text-2xl font-semibold tracking-wide">
              {t('adminNav.relations')}
            </h1>
            <p className="text-xs sm:text-sm text-slate-500">
              {t('admin.erdDescription')}
            </p>
          </div>
        </div>

        {/* Zoom controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => setZoom((z) => Math.min(2, z + 0.15))}
            className="rounded-lg p-2 transition-colors hover:bg-neutral-100"
            title="Zoom in"
          >
            <ZoomIn className="h-4 w-4 text-neutral-600" />
          </button>
          <span className="min-w-[3rem] text-center text-xs font-medium text-neutral-500">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => setZoom((z) => Math.max(0.2, z - 0.15))}
            className="rounded-lg p-2 transition-colors hover:bg-neutral-100"
            title="Zoom out"
          >
            <ZoomOut className="h-4 w-4 text-neutral-600" />
          </button>
          <button
            onClick={fitView}
            className="rounded-lg p-2 transition-colors hover:bg-neutral-100"
            title="Fit to screen"
          >
            <Maximize2 className="h-4 w-4 text-neutral-600" />
          </button>
          <button
            onClick={resetView}
            className="rounded-lg p-2 transition-colors hover:bg-neutral-100"
            title="Reset"
          >
            <RotateCcw className="h-4 w-4 text-neutral-600" />
          </button>
        </div>
      </motion.div>

      {/* Legend */}
      <motion.div variants={staggerItem} className="flex flex-wrap gap-3 px-1">
        {LEGEND_ITEMS.map((item) => (
          <div key={item.label} className="flex items-center gap-1.5">
            <div className="h-3 w-3 rounded" style={{ backgroundColor: item.color }} />
            <span className="text-xs text-neutral-600">{item.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 ms-2">
          <span className="text-xs font-bold text-amber-500">PK</span>
          <span className="text-xs text-neutral-500">Primary Key</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-bold text-indigo-500">FK</span>
          <span className="text-xs text-neutral-500">Foreign Key</span>
        </div>
      </motion.div>

      {/* Canvas */}
      <motion.div
        variants={staggerItem}
        ref={containerRef}
        className="relative overflow-hidden rounded-3xl glass-surface"
        style={{
          height: 'calc(100vh - 220px)',
          backgroundColor: ADMIN_COLORS.surface,
          border: `1px solid ${ADMIN_COLORS.border}`,
          boxShadow: ADMIN_COLORS.shadow,
          cursor: dragging ? 'grabbing' : 'grab',
        }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
      >
        <svg
          width="100%"
          height="100%"
          viewBox={viewBox}
        >
          {/* Relations (behind entities) */}
          {RELATIONS.map((rel, i) => (
            <RelationLine key={i} rel={rel} entities={ENTITIES} />
          ))}
          {/* Entities */}
          {ENTITIES.map((entity) => (
            <EntityBox key={entity.id} entity={entity} />
          ))}
        </svg>
      </motion.div>
    </motion.div>
  );
}
